<template>
  <div class="intro-section mb-5">
    <v-container fluid class="py-5 px-xl-10">
      <v-row class="my-0 py-0">
        <v-col cols="12" md="5" xl="5" class="py-0 pr-xl-16">
          <v-row class="my-0 py-0">
            <v-col cols="12" class="py-0">
              <v-text-field v-model="patientName" label="Patient navn" @keyup.enter="setFilterParams"/>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0">
            <v-col class="py-0">
              <v-menu ref="menuFrom" v-model="menuFrom" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field :value="formattedDateFrom" class="datepicker-input" append-icon="mdi-calendar" @click:append="menuFrom = true" label="Dato" readonly v-on="on"/>
                </template>
                <v-date-picker v-model="dateFrom" no-title @change="menuFrom = false" color="primary lighten-0"/>
              </v-menu>
            </v-col>
            <v-col cols="auto" class="py-0 px-0" align-self="center">
              <v-icon size="15">icon-minus</v-icon>
            </v-col>
            <v-col class="py-0">
              <v-menu ref="menuTo" v-model="menuTo" :close-on-content-click="false" transition="scale-transition" offset-y max-width="290px" min-width="290px">
                <template v-slot:activator="{ on }">
                  <v-text-field :value="formattedDateTo" class="datepicker-input" append-icon="mdi-calendar" @click:append="menuTo = true" readonly v-on="on"/>
                </template>
                <v-date-picker v-model="dateTo" no-title @change="menuTo = false" color="primary lighten-0"/>
              </v-menu>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0">
            <v-col cols="12" md="5" class="py-0">
              <v-text-field label="Visitations nr." v-model="visitNumber" @keyup.enter="setFilterParams"/>
            </v-col>
            <v-col cols="7" md="4" class="py-0 pr-0">
              <v-text-field label="CPR-Nummer" v-model="cprNumber" maxlength="6" @paste.prevent="cprPasteHandler" @input="cprInputHandler" @keyup.enter="setFilterParams"/>
            </v-col>
            <v-col cols="5" md="3" class="py-0">
              <v-text-field ref="cprAdd" v-model="cprNumberAdd" maxlength="4" @keyup.enter="setFilterParams"/>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0" v-if="isAdmin || isSecretary">
            <v-col class="py-0">
              <v-autocomplete :items="doctors" item-text="name" item-value="id" label="Henviser" v-model="selectedDoctor" placeholder="Alle"/>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0">
            <v-col class="py-0">
              <v-autocomplete :items="activeClinics" item-text="name" item-value="id" label="Klinik" v-model="clinicId" :loading="activeClinicsLoading"/>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0" v-if="isAdmin">
            <v-col class="py-0">
              <v-autocomplete :items="surgeons" item-text="fullName" item-value="id" label="Behandler" v-model="selectedSurgeon" placeholder="Alle"/>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0 mb-4">
            <v-col class="py-0">
              <v-text-field v-model="patientEmail" label="Patientens E-mail" @keyup.enter="setFilterParams"/>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0 mb-4">
            <v-col cols="auto" class="py-0 d-flex align-center">
              <div class='intro-section__icon-holder'>
                <v-icon size="23" class="red--text text--lighten-2">mdi-message-processing</v-icon>
              </div>
              <v-checkbox :class="['my-0', 'py-0']" v-model="onlyUnansweredMessage" hide-details @click="allMessagesCheckboxChange">
                <template v-slot:label>
                  Vis alle visitationer med nye beskeder
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <v-row v-if="isSurgeon" class="my-0 py-0 mb-4">
            <v-col cols="auto" class="py-0 d-flex align-center">
              <div class='intro-section__icon-holder'>
                <v-icon size="23" color="#4494A8">mdi-message-processing</v-icon>
              </div>
              <v-checkbox :class="['my-0', 'py-0']" v-model="onlyUnansweredMessageInMyVisits" hide-details @click="myMessagesCheckboxChange">
                <template v-slot:label>
                  Vis mine visitationer med nye beskeder
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <v-row class="my-0 py-0 mb-4">
            <v-col cols="auto" class="py-0 d-flex align-center">
              <div class='intro-section__icon-holder'>
                <v-icon size="21" color="#48aa7b">icon-edi</v-icon>
              </div>
              <v-checkbox :class="['my-0', 'py-0']" v-model="onlyEdi" hide-details>
                <template v-slot:label>
                  EDI
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <v-row v-if="isAdmin" class="my-0 py-0 mb-4">
            <v-col cols="auto" class="py-0 d-flex align-center">
              <div class='intro-section__icon-holder'>
                <div style="width: 20px; height: 20px;background: #f8f5e1; border:1px solid #c5c4c5"/>
              </div>
              <v-checkbox :class="['my-0', 'py-0']" v-model="onlyEmptyExternalDoctorId" hide-details>
                <template v-slot:label>
                  Tandlæge id - mangler
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
          <v-row v-if="isAdmin" class="my-0 py-0 mb-2">
            <v-col cols="auto" class="py-0 d-flex align-center">
              <div class='intro-section__icon-holder'></div>
              <v-checkbox :class="['my-0', 'py-0']" v-model="showTestVisits" hide-details>
                <template v-slot:label>
                  Test henvisning
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="7" xl="7" class="pt-5">
          <span class="grey--text text--darken-2 mb-1 d-block">Status</span>
          <v-row class="my-0">
            <v-col cols="12" sm="6" class="py-0">
              <template v-for="item in availableStatuses.slice(0, 9)">
                <v-checkbox :class="[...checkboxClasses, item.color]" v-model="selectedStatuses" :label="item.label" :value="item.key" hide-details/>
              </template>
            </v-col>
            <v-col cols="12" sm="6" class="py-0">
              <template v-for="item in availableStatuses.slice(9)">
                <v-checkbox :class="[...checkboxClasses, item.color]" v-model="selectedStatuses" :label="item.label" :value="item.key" hide-details/>
              </template>
              <v-checkbox :class="[...checkboxClasses, 'color-gray']" v-model="isSelectAll" @click="selectAll" label="Marker alle" hide-details/>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <div class="intro-section__bottom-box">
      <div class="d-flex justify-end flex-wrap align-center pa-5">
        <v-btn depressed class="my-1" color="primary" outlined @click="clearFilters" :disabled="tableLoading">
          <v-icon left size="25" class="mr-4">icon-close</v-icon>
          Reset filtre
        </v-btn>
        <v-btn depressed class="ml-2 my-1" color="primary" @click="setFilterParams" :disabled="tableLoading">
          <v-icon left size="22" class="mr-4">icon-search</v-icon>
          Søg
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
  import methods from '../../utils/methods';

  export default {
    name: 'OverviewFilter',

    data: function () {
      return {
        checkboxClasses: ['my-0', 'py-0', 'align-center', 'status-checkbox'],
        patientName: '',
        menuFrom: false,
        dateFrom: null,
        menuTo: false,
        dateTo: null,
        visitNumber: '',
        cprNumber: '',
        cprNumberAdd: '',
        selectedDoctor: null,
        selectedSurgeon: null,
        clinicId: null,
        patientEmail: '',
        activeStatuses: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 18, 11, 12, 13, 15, 16],
        activeAdminStatuses: [0, 1, 2, 3, 4, 5, 6, 8, 9, 10, 18, 11, 12, 13, 14, 15, 16, 17],
        selectedStatuses: [],
        isSelectAll: false,
        onlyUnansweredMessage: false,
        onlyUnansweredMessageInMyVisits: false,
        onlyEdi: false,
        showTestVisits: false,
        onlyEmptyExternalDoctorId: false
      };
    },

    mounted: function () {
      this.setSelectedStatuses();
      this.setFilterParams();
      this.$store.dispatch('getActiveClinics');
      this.$store.commit('overview/updateShowDataTable', true);
    },

    computed: {
      role: function () {
        return this.$store.state.role;
      },
      isAdmin: function () {
        return this.$store.getters.isAdmin;
      },
      isSurgeon: function () {
        return this.$store.getters.isSurgeon;
      },
      isSecretary: function () {
        return this.$store.getters.isSecretary;
      },
      doctors: function () {
        let emptyItem = [
          {
            id: null,
            name: 'Alle',
          },
        ];
        if (this.$store.state.doctors.length > 0) {
          return [...emptyItem, ...this.$store.state.doctors];
        } else {
          return [];
        }
      },
      surgeons: function () {
        let emptyItem = [
          {
            id: null,
            fullName: 'Alle',
          },
        ];
        if (this.$store.state.surgeons.length > 0) {
          return [...emptyItem, ...this.$store.state.surgeons];
        } else {
          return [];
        }
      },
      availableStatuses: function () {
        return this.$store.state.overview.statusTypes;
      },
      tableLoading: function () {
        return this.$store.state.overview.tableLoading;
      },
      formattedDateFrom: function () {
        if (this.dateFrom) {
          return methods.formatDate(this.dateFrom);
        } else {
          return 'Fra';
        }
      },
      formattedDateTo: function () {
        if (this.dateTo) {
          return methods.formatDate(this.dateTo);
        } else {
          return 'Til';
        }
      },
      activeClinics: function () {
        let defaultItem = [
          {
            id: null,
            name: 'Alle',
          },
        ];
        if (this.$store.state.activeClinics.length > 0) {
          return [...defaultItem, ...this.$store.state.activeClinics];
        } else {
          return defaultItem;
        }
      },
      activeClinicsLoading: function() {
        return this.$store.state.activeClinicsLoading;
      },
    },

    methods: {
      cprPasteHandler: function(event) {
        const clipboardData = event.clipboardData || window.clipboardData;
        const pastedData = clipboardData.getData('Text');
        if (pastedData?.length > 6) {
          if (pastedData.includes('-')) {
            this.cprNumber = pastedData.split('-')[0].slice(0, 6);
            this.cprNumberAdd = pastedData.split('-')[1].slice(0, 4);
          } else {
            this.cprNumber = pastedData.slice(0, 6);
            this.cprNumberAdd = pastedData.slice(6, 10);
          }
          this.$refs.cprAdd.focus();
        } else if (pastedData?.length === 6) {
          this.cprNumber = pastedData;
          this.$refs.cprAdd.focus();
        } else {
          this.cprNumber = pastedData;
        }
      },
      cprInputHandler: async function (value) {
        if (value?.length === 6) {
          this.$refs.cprAdd.focus();
        }
      },
      setSelectedStatuses: function () {
        if (this.isAdmin) {
          this.selectedStatuses = [];
          this.selectedStatuses = this.activeAdminStatuses;
        } else {
          this.selectedStatuses = [];
          this.selectedStatuses = this.activeStatuses;
        }
      },
      selectAll: function () {
        if (this.isSelectAll) {
          this.selectedStatuses = [];
          this.availableStatuses.forEach((item) => {
            this.selectedStatuses.push(item.key);
          });
        } else {
          this.selectedStatuses = [];
        }
      },
      clearFilters: function () {
        this.patientName = '';
        this.visitNumber = '';
        this.cprNumber = '';
        this.cprNumberAdd = '';
        this.dateFrom = null;
        this.dateTo = null;
        this.selectedDoctor = null;
        this.selectedSurgeon = null;
        this.clinicId = null;
        this.patientEmail = '';
        this.isSelectAll = false;
        this.onlyUnansweredMessage = false;
        this.onlyUnansweredMessageInMyVisits = false;
        this.onlyEdi = false;
        this.showTestVisits = false;
        this.onlyEmptyExternalDoctorId = false;
        this.setSelectedStatuses();
        this.setFilterParams();
      },
      filterParams: function () {
        let params = {};
        this.patientName && (params.PatientName = this.patientName);
        this.dateFrom && (params.DateFrom = new Date(this.dateFrom).toISOString());
        this.dateTo && (params.DateTo = new Date(this.dateTo).toISOString());
        this.visitNumber && (params.VisitNumber = parseInt(this.visitNumber));
        if (this.cprNumber || this.cprNumberAdd) {
          if (this.cprNumberAdd) {
            params.Cpr = `${this.cprNumber}-${this.cprNumberAdd}`;
          } else {
            params.Cpr = this.cprNumber;
          }
        }
        this.selectedDoctor && (params.DoctorId = this.selectedDoctor);
        this.selectedSurgeon && (params.SurgeonId = this.selectedSurgeon);
        this.clinicId && (params.ClinicId = this.clinicId);
        this.patientEmail && (params.PatientEmail = this.patientEmail);
        this.onlyUnansweredMessage && (params.OnlyUnansweredMessage = this.onlyUnansweredMessage);
        this.onlyUnansweredMessageInMyVisits && (params.OnlyUnansweredMessageInMyVisits = this.onlyUnansweredMessageInMyVisits);
        this.onlyEdi && (params.OnlyEdi = this.onlyEdi);
        this.showTestVisits && (params.IsOnlyTestVisits = this.showTestVisits);
        this.onlyEmptyExternalDoctorId && (params.onlyEmptyExternalDoctorId = this.onlyEmptyExternalDoctorId);
        methods.checkLength(this.selectedStatuses) && (params.Statuses = this.selectedStatuses);
        return params;
      },
      setFilterParams: function () {
        if (!this.tableLoading) {
          this.$store.commit('overview/updateFilterParams', this.filterParams());
        }
      },
      allMessagesCheckboxChange: function() {
        this.onlyUnansweredMessageInMyVisits = false;
      },
      myMessagesCheckboxChange: function() {
        this.onlyUnansweredMessage = false;
      }
    },
  };
</script>
